import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiSearch, FiPlus, FiMinus, FiTrash2, FiShoppingCart, FiCreditCard, FiSmartphone, FiDollarSign, FiList, FiGrid, FiPercent } from 'react-icons/fi';

const NewSale = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchProduct, setSearchProduct] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paidAmount, setPaidAmount] = useState('');
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('amount');
  const [newClient, setNewClient] = useState({ name: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list');

  const canCreateSale = user?.role === 'admin' || user?.role === 'caissier';
  
  useEffect(() => { 
    if (!canCreateSale) {
      toast.error('Vous n\'avez pas l\'autorisation de créer des ventes');
      navigate('/dashboard');
    }
    fetchProducts(); 
    fetchClients(); 
  }, [searchProduct]);

  const fetchProducts = async () => {
    try {
      const response = await api.get(`/products?search=${searchProduct}`);
      setProducts(response.data);
    } catch (error) { console.error(error); }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) { console.error(error); }
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.product_id === product.id);
    if (existingItem) {
      if (existingItem.quantity + 1 > product.stock) { 
        toast.error(`Stock insuffisant. Maximum: ${product.stock}`); 
        return; 
      }
      setCart(cart.map(item => 
        item.product_id === product.id 
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * parseFloat(item.unit_price) } 
          : item
      ));
    } else {
      if (product.stock < 1) { 
        toast.error('Produit en rupture de stock'); 
        return; 
      }
      setCart([...cart, { 
        product_id: product.id, 
        product_name: product.name, 
        quantity: 1, 
        unit_price: parseFloat(product.sale_price), 
        total: parseFloat(product.sale_price), 
        max_stock: product.stock 
      }]);
    }
    toast.success(`${product.name} ajouté au panier`);
  };

  const updateQuantity = (productId, newQuantity) => {
    const item = cart.find(i => i.product_id === productId);
    if (newQuantity > item.max_stock) { 
      toast.error(`Stock maximum: ${item.max_stock}`); 
      return; 
    }
    if (newQuantity < 1) { 
      removeFromCart(productId); 
      return; 
    }
    setCart(cart.map(item => 
      item.product_id === productId 
        ? { ...item, quantity: newQuantity, total: newQuantity * parseFloat(item.unit_price) } 
        : item
    ));
  };

  const removeFromCart = (productId) => { 
    setCart(cart.filter(item => item.product_id !== productId)); 
    toast.success('Produit retiré du panier'); 
  };

  // Calcul des totaux - avec parseFloat pour éviter les erreurs
  const subtotal = cart.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  
  // Calcul de la remise
  let discountAmount = 0;
  if (discountType === 'amount') {
    discountAmount = Math.min(parseFloat(discount) || 0, subtotal);
  } else {
    discountAmount = (subtotal * (parseFloat(discount) || 0)) / 100;
  }
  
  const totalAfterDiscount = subtotal - discountAmount;
  const paidAmountValue = parseFloat(paidAmount) || 0;
  const remainingToPay = totalAfterDiscount - paidAmountValue;

  const handleDiscountChange = (value) => {
    const numValue = parseFloat(value) || 0;
    setDiscount(numValue);
  };

  const handleDiscountTypeChange = (type) => {
    setDiscountType(type);
    setDiscount(0);
  };

  const handleCreateClient = async () => {
    if (!newClient.name || !newClient.phone) { 
      toast.error('Nom et téléphone sont requis'); 
      return; 
    }
    try {
      const response = await api.post('/clients', newClient);
      const createdClient = { id: response.data.id, name: newClient.name, phone: newClient.phone };
      setClients([...clients, createdClient]);
      setSelectedClient(createdClient);
      setShowClientModal(false);
      setNewClient({ name: '', phone: '', email: '' });
      toast.success('Client ajouté avec succès');
    } catch (error) { 
      toast.error('Erreur lors de l\'ajout du client'); 
    }
  };

  const validateSale = async () => {
    if (cart.length === 0) { 
      toast.error('Ajoutez des produits au panier'); 
      return; 
    }
    if (!selectedClient) { 
      toast.error('Sélectionnez un client'); 
      return; 
    }
    if (paymentMethod !== 'credit' && (!paidAmount || paidAmountValue <= 0)) {
      toast.error('Veuillez saisir le montant payé');
      return;
    }
    
    setLoading(true);
    try {
      // Préparer les items avec les bons types
      const itemsData = cart.map(item => ({ 
        product_id: item.product_id, 
        quantity: item.quantity, 
        unit_price: parseFloat(item.unit_price)
      }));
      
      const saleData = { 
        client_id: selectedClient.id, 
        items: itemsData, 
        total_amount: parseFloat(totalAfterDiscount), 
        discount: parseFloat(discountAmount), 
        paid_amount: paidAmountValue, 
        payment_method: paymentMethod 
      };
      
      console.log('Données de la vente envoyées:', saleData);
      const response = await api.post('/sales', saleData);
      toast.success('Vente enregistrée avec succès !');
      navigate('/invoices');
    } catch (error) { 
      console.error('Erreur:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'enregistrement de la vente'); 
    } finally { 
      setLoading(false); 
    }
  };

  const formatAmount = (amount) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(amount || 0);

  const filteredProducts = products.filter(p => p.stock > 0);

  if (!canCreateSale) return null;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Nouvelle vente</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Products */}
        <div className="lg:col-span-2">
          <div className="card mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Rechercher des produits</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}
                  title="Vue liste"
                >
                  <FiList size={18} />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}
                  title="Vue grille"
                >
                  <FiGrid size={18} />
                </button>
              </div>
            </div>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input 
                type="text" 
                placeholder="Rechercher par nom ou SKU..." 
                className="input-field pl-10" 
                value={searchProduct} 
                onChange={(e) => setSearchProduct(e.target.value)} 
              />
            </div>
          </div>

          {/* Vue Liste des produits */}
          {viewMode === 'list' && (
            <div className="card overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr className="text-left">
                    <th className="pb-3 font-medium text-gray-600">Code</th>
                    <th className="pb-3 font-medium text-gray-600">Désignation</th>
                    <th className="pb-3 font-medium text-gray-600 text-right">Prix</th>
                    <th className="pb-3 font-medium text-gray-600 text-center">Stock</th>
                    <th className="pb-3 font-medium text-gray-600 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.slice(0, 50).map((product) => (
                    <tr key={product.id} className="border-b last:border-0 hover:bg-gray-50 transition-colors">
                      <td className="py-3 text-sm text-gray-500">{product.sku || '---'}</td>
                      <td className="py-3 font-medium text-gray-800">{product.name}</td>
                      <td className="py-3 text-right text-green-600 font-semibold">{formatAmount(product.sale_price)}</td>
                      <td className="py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${product.stock <= product.min_stock ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                          {product.stock}
                        </span>
                      </td>
                      <td className="py-3 text-center">
                        <button
                          onClick={() => addToCart(product)}
                          className="bg-green-600 text-white px-4 py-1.5 rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-1 mx-auto"
                        >
                          <FiPlus size={14} />
                          Ajouter
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredProducts.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">Aucun produit trouvé</p>
                </div>
              )}
            </div>
          )}

          {/* Vue Grille des produits */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto">
              {filteredProducts.slice(0, 20).map((product) => (
                <button
                  key={product.id}
                  onClick={() => addToCart(product)}
                  className="card text-left hover:shadow-md transition-shadow"
                >
                  <h3 className="font-medium text-gray-800">{product.name}</h3>
                  <p className="text-green-600 font-bold mt-2">{formatAmount(product.sale_price)}</p>
                  <p className="text-xs text-gray-500">Stock: {product.stock}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right Panel - Cart & Checkout */}
        <div className="lg:col-span-1">
          <div className="card mb-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Client</h2>
              <button onClick={() => setShowClientModal(true)} className="text-green-600 text-sm flex items-center gap-1">
                <FiPlus size={14} />
                Nouveau client
              </button>
            </div>
            
            {selectedClient ? (
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="font-medium">{selectedClient.name}</p>
                <p className="text-sm text-gray-600">{selectedClient.phone}</p>
                <button onClick={() => setSelectedClient(null)} className="text-xs text-red-600 mt-1">
                  Changer
                </button>
              </div>
            ) : (
              <select
                className="input-field"
                onChange={(e) => {
                  const client = clients.find(c => c.id === parseInt(e.target.value));
                  setSelectedClient(client);
                }}
                defaultValue=""
              >
                <option value="" disabled>Sélectionner un client</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name} - {client.phone}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Panier ({cart.length} articles)</h2>
            
            {cart.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Panier vide</p>
            ) : (
              <>
                <div className="space-y-3 max-h-80 overflow-y-auto mb-4">
                  {cart.map((item) => (
                    <div key={item.product_id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.product_name}</p>
                        <p className="text-xs text-gray-500">{formatAmount(item.unit_price)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <FiMinus size={14} />
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <FiPlus size={14} />
                        </button>
                        <button
                          onClick={() => removeFromCart(item.product_id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded ml-2"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4 space-y-4">
                  {/* Sous-total */}
                  <div className="flex justify-between items-center pb-2 border-b">
                    <span className="text-gray-600">Sous-total</span>
                    <span className="font-bold text-lg text-gray-800">{formatAmount(subtotal)}</span>
                  </div>
                  
                  {/* Section Remise */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-700 font-medium flex items-center gap-1">
                        <FiPercent size={14} />
                        Remise
                      </span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleDiscountTypeChange('amount')}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${discountType === 'amount' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        >
                          FCFA
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDiscountTypeChange('percent')}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${discountType === 'percent' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        >
                          %
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2 items-center">
                      <input
                        type="number"
                        className="input-field flex-1"
                        value={discount}
                        onChange={(e) => handleDiscountChange(e.target.value)}
                        min="0"
                        max={discountType === 'amount' ? subtotal : 100}
                        step={discountType === 'amount' ? 100 : 1}
                        placeholder={discountType === 'amount' ? 'Montant en FCFA' : 'Pourcentage %'}
                      />
                      <span className="text-green-600 font-semibold whitespace-nowrap">
                        - {formatAmount(discountAmount)}
                      </span>
                    </div>
                  </div>
                  
                  {/* TOTAL GÉNÉRAL */}
                  <div className="flex justify-between items-center pt-2 bg-green-50 rounded-lg p-3">
                    <span className="text-gray-800 font-bold text-lg">TOTAL GÉNÉRAL</span>
                    <span className="text-green-600 font-bold text-2xl">{formatAmount(totalAfterDiscount)}</span>
                  </div>

                  {/* Mode de paiement */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mode de paiement</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('cash')}
                        className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors ${paymentMethod === 'cash' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        <FiDollarSign size={16} />
                        Espèces
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('mobile_money')}
                        className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors ${paymentMethod === 'mobile_money' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        <FiSmartphone size={16} />
                        Mobile Money
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('credit')}
                        className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors ${paymentMethod === 'credit' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        <FiCreditCard size={16} />
                        Crédit
                      </button>
                    </div>
                  </div>

                  {/* Montant payé (sauf pour crédit) */}
                  {paymentMethod !== 'credit' && (
                    <div className="bg-blue-50 rounded-lg p-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Montant payé</label>
                      <input
                        type="number"
                        className="input-field"
                        value={paidAmount}
                        onChange={(e) => setPaidAmount(e.target.value)}
                        placeholder="0"
                        min="0"
                        max={totalAfterDiscount}
                      />
                      {remainingToPay > 0 && (
                        <p className="text-sm text-orange-600 mt-2 flex items-center gap-1 font-medium">
                          ⚠️ Reste à payer: {formatAmount(remainingToPay)}
                        </p>
                      )}
                      {remainingToPay < 0 && (
                        <p className="text-sm text-green-600 mt-2 flex items-center gap-1 font-medium">
                          ✓ Monnaie à rendre: {formatAmount(Math.abs(remainingToPay))}
                        </p>
                      )}
                      {remainingToPay === 0 && paidAmount !== '' && paidAmountValue > 0 && (
                        <p className="text-sm text-green-600 mt-2 flex items-center gap-1 font-medium">
                          ✓ Montant parfaitement payé
                        </p>
                      )}
                    </div>
                  )}

                  {paymentMethod === 'credit' && (
                    <div className="bg-orange-50 rounded-lg p-3">
                      <p className="text-sm text-orange-600 flex items-center gap-1">
                        <FiCreditCard size={14} />
                        Paiement à crédit - La dette de {formatAmount(totalAfterDiscount)} sera ajoutée au client
                      </p>
                    </div>
                  )}

                  {/* Récapitulatif avant validation */}
                  <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-2 text-center">
                    <p>Articles: {cart.length} | Total: {formatAmount(totalAfterDiscount)}</p>
                  </div>

                  {/* Bouton validation */}
                  <button
                    onClick={validateSale}
                    disabled={loading || cart.length === 0 || (paymentMethod !== 'credit' && (!paidAmount || paidAmountValue <= 0))}
                    className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 mt-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <FiShoppingCart size={18} />
                        Valider la vente
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal Nouveau Client */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Nouveau client</h2>
              <button onClick={() => setShowClientModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <FiPlus className="text-gray-500" size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  className="input-field"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  placeholder="Nom complet"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                <input
                  type="tel"
                  className="input-field"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  placeholder="6XXXXXXXX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  className="input-field"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  placeholder="email@exemple.com"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowClientModal(false)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                  Annuler
                </button>
                <button onClick={handleCreateClient} className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors">
                  Ajouter le client
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewSale;